import React, { useContext, useState, useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../../components/global/appContext";
import Form from "./Form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import lmsAPI from "../../../../services/lmsAPI";
import { useGetLocation } from "../../../../hooks/queries/locations";

export default function LocationEdit(props) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const queryClient = useQueryClient();

  let { id } = useParams();
  const { data: fetchedLocation, isError, isLoading } = useGetLocation(id);

  const [location, setLocation] = useState(null);

  const [errors, setErrors] = useState({});
  const [deleted, setDeleted] = useState(false);

  // Sync fetched data with local state
  useEffect(() => {
    if (fetchedLocation) {
      setLocation(fetchedLocation);
    }
  }, [fetchedLocation]);

  async function deleteLocationRequest() {
    const res = await lmsAPI.delete("/locations/" + id);
    return res.data;
  }

  const deleteLocation = useMutation(deleteLocationRequest, {
    onSuccess: (data) => {
      appContext.setMessage(t("location.table.messages.deletedSuccessfully"));
      setDeleted(true);
    },
    onError: (error) => {
      appContext.setMessage(error.response.data.message, true);
    },
  });

  async function saveLocationRequest() {
    const res = await lmsAPI.put("/locations/" + id, {
      location: location,
    });
    return res.data;
  }

  const saveLocation = useMutation(saveLocationRequest, {
    onSuccess: (data) => {
      setLocation(data);
      queryClient.setQueryData(["locations", id], data);
      appContext.setMessage(t("location.validation.updateSuccess"));
    },
    onError: (error) => {
      appContext.setMessage(error.response.data.message, true);
      console.error(error.response);
      const baseError =
        error.response.data.base && error.response.data.base.join(" ");
      appContext.setMessage(baseError || t("location.validation.checkFields"), true);
      setErrors(error.response.data);
      if (error.response.data.message) {
        appContext.setMessage(error.response.data.message);
      }
    },
  });

  if (deleted) {
    return <Navigate to={"/places/locations"} />;
  }

  if (isError) {
    return <p>{t("location.errors.loading")}</p>;
  }

  if (isLoading || !location) {
    return <p>{t("common.loading")}</p>;
  }

  return (
    <Form
      title={location && location.name}
      location={location}
      setLocation={setLocation}
      saveAction={{
        content: t("location.actions.save"),
        onAction: saveLocation.mutate,
        loading: saveLocation.isLoading,
      }}
      // pagePrimaryAction={}
      pageSecondaryActions={
        location && [
          {
            content: t("location.actions.preview"),
            url: location.preview_url,
            external: true,
            disabled: location.status !== "published",
          },
        ]
      }
      footerSecondaryActions={[
        {
          content: t("location.actions.delete"),
          destructive: true,
          outline: true,
          onAction: deleteLocation.mutate,
          loading: deleteLocation.isLoading,
        },
      ]}
      loading={saveLocation.isLoading || deleteLocation.isLoading}
      errors={errors}
    />
  );
}
